import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useToasts } from "../../contexts/toasts";
import { User } from "../../types/User";
import { Users } from "../../api/Users";
import { PagedResults } from "../../types/generics";
import {
  Alert,
  Button,
  Col,
  Container,
  Modal,
  Row,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faInfoCircle,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
import {
  ConfigProvider,
  Pagination,
  Select,
  Space,
  Switch,
} from "antd";
import itIT from "antd/lib/locale/it_IT";
import AppSearch from "../../common/AppSearch";
import { debounce } from "lodash";
import { useAuth } from "../../contexts/auth";
import { RoleLabels, Roles } from "../../enums/Roles";
import { Sesso } from "../../enums/Sesso";
import { TailSpin } from "react-loader-spinner";
import { useNavigate } from "react-router-dom";
import UserItem from "../../Components/list-items/UserItem";

function UsersList() {
  const { showToast } = useToasts();
  const { loggedUser } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>();
  const [error, setError] = useState(false);
  const [totalUsers, setTotalUsers] = useState(0);
  const [filters, setFilters] = useState<{
    sesso?: Sesso;
    pageSize: number;
    currentPage: number;
    freeText: string;
    active?: boolean;
    role?: Roles;
  }>({
    sesso: undefined,
    pageSize: 20,
    currentPage: 1,
    freeText: "",
    active: false,
    role: undefined,
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User>();
  const [loading, setLoading] = useState(false);

  const initUsers = useCallback(() => {
    const urlParams = new URLSearchParams();
    urlParams.append("page", `${filters.currentPage}`);
    urlParams.append("limit", `${filters.pageSize}`);
    if (loggedUser && loggedUser!.role === Roles.Trainer)
      urlParams.append("parentId", `${loggedUser!.id}`);
    if (filters.freeText !== "") {
      urlParams.append("freeText", filters.freeText);
    }
    if (filters.sesso) urlParams.append("sesso", filters.sesso);
    if (filters.active) urlParams.append("active", `${filters.active}`);
    if (filters.role) urlParams.append("role", `${filters.role}`);
    Users.getUsers(urlParams).then(
      ({ data }) => {
        setUsers((data as PagedResults<User>).items);
        setTotalUsers((data as PagedResults<User>).totalItems);
      },
      () => {
        showToast(
          "users-error",
          "Impossibile ottenere la lista degli utenti",
          "",
          "danger"
        );
        setUsers([]);
        setError(true);
      }
    );
  }, [filters, showToast, loggedUser]);

  const reset = useCallback(() => {
    setError(false);
    setUsers(undefined);
    setTotalUsers(0);
    initUsers();
  }, [initUsers]);

  useEffect(() => reset(), [reset, filters]);

  const handlePageChange = useCallback((page: number) => {
    setFilters((previous) => ({
      ...previous,
      currentPage: page,
    }));
  }, []);

  const handlePageSizeChange = useCallback((_current: number, size: number) => {
    setFilters((previous) => ({
      ...previous,
      pageSize: size,
      currentPage: 1,
    }));
  }, []);

  const handleFilterChange = useCallback((text: string) => {
    setFilters((previous) => ({
      ...previous,
      currentPage: 1,
      freeText: text,
    }));
  }, []);

  const handleFieldChange = useCallback(
    (field: string) => (evt: any) => {
      setFilters((previous) => ({
        ...previous,
        [field]: evt,
      }));
    },
    []
  );

  const debounceSearchChange = useMemo(
    () => debounce(handleFilterChange, 500),
    [handleFilterChange]
  );

  useEffect(() => {
    return () => {
      debounceSearchChange.cancel();
    };
  }, [debounceSearchChange]);

  const renderToolbar = () => {
    return (
      <>
        <Row className="mb-4 align-items-center justify-content-between">
          <Col className="mb-md-0" xs={6} md="auto">
            <h4 className="mb-0">I tuoi Utenti</h4>
          </Col>
          <Col xs={12} md="auto" className="d-none d-md-flex">
            <Space align="center">
              <AppSearch
                className="w-auto"
                placeholder="Cerca per nome, cognome o email..."
                onChange={debounceSearchChange}
              />
              <Select
                value={filters.sesso}
                placeholder="Cerca per sesso..."
                className="w-100"
                onChange={handleFieldChange("sesso")}
              >
                <Select.Option key={Sesso.Male}>{Sesso.Male}</Select.Option>
                <Select.Option key={Sesso.Female}>{Sesso.Female}</Select.Option>
              </Select>
              <Select
                value={filters.role}
                placeholder="Cerca per ruolo..."
                className="w-100"
                onChange={handleFieldChange("role")}
              >
                <Select.Option key={Roles.Athlete}>
                  {RoleLabels[Roles.Athlete]}
                </Select.Option>
                <Select.Option key={Roles.Trainer}>
                  {RoleLabels[Roles.Trainer]}
                </Select.Option>
                <Select.Option key={Roles.Superadmin}>
                  {RoleLabels[Roles.Superadmin]}
                </Select.Option>
              </Select>
              <Switch
                checked={filters.active}
                onChange={handleFieldChange("active")}
                checkedChildren="Attivi"
                unCheckedChildren="Tutti"
              />
            </Space>
          </Col>
          <Col className="text-end" xs={6} md="auto">
            <button onClick={createUser} className="button-primary">
              <FontAwesomeIcon icon={faPlus} />
              <span className="d-none d-sm-inline ms-2">Aggiungi Utente</span>
            </button>
          </Col>
        </Row>
        <Row className="d-flex d-md-none mb-3">
          <Col md="auto" xs={6}>
            <AppSearch
              className="w-auto"
              placeholder="Cerca per nome, cognome o email..."
              onChange={debounceSearchChange}
            />
          </Col>
          <Col md="auto" xs={6}>
            <Select
              value={filters.sesso}
              placeholder="Cerca per sesso..."
              className="w-100"
              onChange={handleFieldChange("sesso")}
            >
              <Select.Option key={Sesso.Male}>{Sesso.Male}</Select.Option>
              <Select.Option key={Sesso.Female}>{Sesso.Female}</Select.Option>
            </Select>
          </Col>
          <Col md="auto" xs={6} className="mt-3">
            <Select
              value={filters.role}
              placeholder="Cerca per ruolo..."
              className="w-100"
              onChange={handleFieldChange("role")}
            >
              <Select.Option key={Roles.Athlete}>{Roles.Athlete}</Select.Option>
              <Select.Option key={Roles.Trainer}>{Roles.Trainer}</Select.Option>
              <Select.Option key={Roles.Superadmin}>
                {Roles.Superadmin}
              </Select.Option>
            </Select>
          </Col>
          <Col md="auto" xs={6} className="mt-3 text-center">
            <Switch
              checked={filters.active}
              onChange={handleFieldChange("active")}
              checkedChildren="Attivi"
              unCheckedChildren="Tutti"
            />
          </Col>
        </Row>
        <Row className="mb-4 align-items-center justify-content-center">
          <ConfigProvider locale={itIT}>
            <Pagination
              disabled={!users}
              total={totalUsers || 0}
              showTotal={(total, range) =>
                `${range[0]}-${range[1]} di ${total}`
              }
              pageSize={filters.pageSize}
              current={filters.currentPage}
              onChange={handlePageChange}
              onShowSizeChange={handlePageSizeChange}
              showSizeChanger
              align="center"
            />
          </ConfigProvider>
        </Row>
      </>
    );
  };

  const onDeleteUser = useCallback((user: User) => {
    setShowDeleteModal(true);
    setSelectedUser(user);
  }, []);

  const onDetailClick = useCallback(
    (user: User) => {
      navigate(`${user.id}`);
    },
    [navigate]
  );

  const createUser = useCallback(() => navigate("nuovo"), [navigate]);

  const renderUsers = useCallback(() => {
    if (error)
      return (
        <Alert variant="danger" className="py-2">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span className="ms-2">
            C'è stato un errore, per favore riprova più tardi
          </span>
        </Alert>
      );

    return users!.length === 0 ? (
      <Alert variant="info" className="py-2">
        <FontAwesomeIcon icon={faInfoCircle} />
        <span className="ms-2">Nessun Utente trovato</span>
      </Alert>
    ) : (
      <Row>
        {users!.map((user, index) => (
          <Col xs={12} md={4} className="p-2" key={index}>
            <UserItem
              user={user}
              onDelete={onDeleteUser}
              onEdit={onDetailClick}
              onShow={onDetailClick}
            />
          </Col>
        ))}
      </Row>
    );
  }, [error, users, onDeleteUser, onDetailClick]);

  const renderAsyncContent = useCallback(() => {
    return users ? (
      renderUsers()
    ) : (
      <div className="d-flex justify-content-center py-5 my-5">
        <TailSpin height={100} width={100} color="#9c1419" radius="1" />
      </div>
    );
  }, [users, renderUsers]);

  const handleDeleteClose = () => {
    setShowDeleteModal(false);
    setSelectedUser(undefined);
  };

  const deleteUser = useCallback(() => {
    setLoading(true);
    Users.deleteUser(selectedUser!.id!).then(
      () => {
        showToast(
          "user-delete-success",
          "Utente cancellato correttamente",
          "",
          "success"
        );
        setLoading(false);
        reset();
        handleDeleteClose();
      },
      () => {
        setLoading(false);
        showToast(
          "user-delete-error",
          "Errore nella cancellazione dell'utente",
          "",
          "danger"
        );
      }
    );
  }, [selectedUser, showToast, reset]);

  return (
    <Container fluid="md" className="mt-5">
      {renderToolbar()}
      <hr />
      {renderAsyncContent()}

      <Modal show={showDeleteModal} onHide={handleDeleteClose}>
        <Modal.Header closeButton>
          <Modal.Title>Cancella Utente</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Sei sicuro di voler cancellare l'utente{" "}
            <strong>
              {selectedUser?.nome} {selectedUser?.cognome}
            </strong>
            ?
          </p>

          <Alert variant="warning">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <span className="ms-2">
              Questa azione non può essere annullata.
            </span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            size="sm"
            className="border"
            onClick={handleDeleteClose}
          >
            Chiudi
          </Button>
          <Button variant="danger" size="sm" onClick={deleteUser}>
            {loading ? (
              <TailSpin width={20} height={20} />
            ) : (
              <span>Cancella</span>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default memo(UsersList);
